/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { toastSuccess, toastError } from "../../../utils";
import "./styles/EditChaptersOrEpisodes.scss";
import AppContext from "../../../context/appContext";
import UploadedDramaturgy from "../../../components/UploadedFile/UploadInputs/UploadInputDramaturgy";
import {
  editMaterialPlanningAttachment,
  editBlockChapterPlanningAttachment,
} from "../../../services/api/attachments/attachments.api";
import {
  editChapterAddendum,
  editEpisodeAddendum,
} from "../../../services/api/attachments/addendum.api";
import FormAndFields from "../CreateAndUpdatePlannings/FormAndFields/FormAndFields";
import { compileOptionsForChapters } from "../compileOptions";
import AlertBox from "../../../components/AlertBox/AlertBox";

const toLocalISOString = (date) => {
  if (!date) return null;
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 19);
};

const EditPlanningAttachment = (props) => {
  const { file, listItems, reload, typeValues, action } = props;

  const appContext = useContext(AppContext);

  const { type, deliveryControl } = file;
  const completionDates = deliveryControl?.completionDates || {};
  const structuralRevision = deliveryControl?.structuralRevision || null;

  const getInitialAddendumIds = () => {
    if (!file.isAddendum) return [];
    if (file.isChapter) {
      return (listItems.blocks || [])
        .flatMap((b) => b.chapters || [])
        .filter((ch) => ch.addenda?.some((a) => a.attachmentId === file.id))
        .map((ch) => ch.id);
    }
    if (file.isMaterial) {
      const seen = new Set();
      return (listItems.materials || [])
        .filter((m) => m.addenda?.some((a) => a.attachmentId === file.id))
        .map((m) => m.episodeId)
        .filter((id) => {
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
    }
    return [];
  };

  const initialAddendumIds = getInitialAddendumIds();

  const [notDefault, setNotDefault] = useState(false);
  const [stateEdit, setStateEdit] = useState({
    isMaterial: file.isMaterial,
    isChapter: file.isChapter,
    isAddendum: file.isAddendum,
    ...(file.isAddendum &&
      file.isChapter && { chapterIds: initialAddendumIds }),
    ...(file.isAddendum &&
      file.isMaterial && { episodeIds: initialAddendumIds }),
    id: file.chapterId,
    chapterId: null,
    blockId: null,
    version: file.version || 1,
    typeId: type?.id,
    receivedAt: completionDates.received || "",
    attachmentTypeId: type?.id,
    chapterOrEpisodeNumber:
      listItems?.materials?.find((m) => m.id === file.materialId)
        ?.episodeNumber ?? null,
    structuringReview: !!structuralRevision,
    reason: structuralRevision ? String(structuralRevision.id) : null,
    planning: {
      realizedContentSight: completionDates.contentAssessment
        ? new Date(completionDates.contentAssessment)
        : null,
      realizedProdutionSight: completionDates.productionAssessment
        ? new Date(completionDates.productionAssessment)
        : null,
      diversityAssessment: completionDates.diversityAssessment
        ? new Date(completionDates.diversityAssessment)
        : null,
      realizedChannelFeedback: completionDates.channelNotes
        ? new Date(completionDates.channelNotes)
        : null,
    },
  });

  const handleStartDates = {
    editAttachment: {
      realizedContentSight: "",
      realizedProdutionSight: "",
      realizedArtisticApproval: "",
      realizedChannelFeedback: "",
    },
  };

  const handleMaxDates = {
    editAttachment: {
      realizedContentSight: "",
      realizedProdutionSight: "",
      realizedArtisticApproval: "",
      realizedChannelFeedback: "",
    },
  };

  const dramaturgyList = compileOptionsForChapters(listItems.blocks);
  const materialsList = listItems?.materials;

  const getDate = useCallback(
    (date) => {
      if (date && date !== stateEdit.receivedAt) {
        setStateEdit((prev) => ({
          ...prev,
          receivedAt: date,
        }));
      }
    },
    [stateEdit.receivedAt]
  );

  const handleChangeByInputName = useCallback((value, inputName) => {
    setStateEdit((prev) => {
      if (prev.planning && inputName in prev.planning) {
        return {
          ...prev,
          planning: {
            ...prev.planning,
            [inputName]: value,
          },
        };
      }
      return {
        ...prev,
        [inputName]: value,
      };
    });
  }, []);

  const setStructuringReview = useCallback((has) => {
    setNotDefault(true);
    setStateEdit((prev) => ({
      ...prev,
      structuringReview: has,
      reason: has ? prev.reason : null,
    }));
  }, []);

  const getReason = useCallback((ev) => {
    const value = ev?.target?.value;
    setStateEdit((prev) => {
      if (prev.structuringReview) {
        return { ...prev, reason: value };
      }
      return { ...prev, reason: null };
    });
  }, []);

  const getVersion = useCallback((versionValue) => {
    setStateEdit((prev) => ({
      ...prev,
      version: versionValue,
    }));
  }, []);

  const setEpisodeIds = useCallback((ids) => {
    setStateEdit((prev) => ({
      ...prev,
      ...(prev.isChapter ? { chapterIds: ids } : { episodeIds: ids }),
    }));
  }, []);

  const handleEpisodeChange = (selectedId) => {
    const selectedMaterial = materialsList.find(
      (material) => Number(material.attachmentTypeId) === 1211
    );

    const planning = selectedMaterial?.plannings?.find(
      (p) => Number(p.chapterOrEpisodeNumber) === selectedId
    );

    const latestVersion = planning?.versions
      ?.slice()
      .sort((a, b) => b.version - a.version)[0];

    const resolvedVersion = latestVersion?.version || 1;

    const flatMaterial = materialsList?.find(
      (material) =>
        Number(material.type?.id ?? material.attachmentTypeId) === 1211 &&
        Number(material.episodeNumber) === selectedId &&
        Number(material.version) === resolvedVersion
    );

    setStateEdit((prev) => ({
      ...prev,
      chapterOrEpisodeNumber: selectedId,
      chapterNumber: selectedId,
      version: resolvedVersion,
      newMaterialId: flatMaterial?.id ?? null,
      scheduleDelivery: latestVersion?.scheduleDelivery || null,
      scheduleRelease: latestVersion?.scheduleRelease || null,
      rescheduledDelivery: latestVersion?.rescheduledDelivery || null,
      rescheduledRelease: latestVersion?.rescheduledRelease || null,
    }));
  };

  const handleChapterChange = (selectedId) => {
    const chapter = dramaturgyList.find(
      (chapterItem) => Number(chapterItem?.id) === selectedId
    );

    if (!chapter) return;

    setStateEdit((prev) => ({
      ...prev,
      id: chapter.id,
      chapterId: chapter.id,
      blockId: chapter.blockId,
      chapterNumber: chapter.chaption,
      blockNumber: chapter.block,
      numberChaptersBlock: chapter.numberChaptersBlock,
      numberAttachmentsEpisodes: chapter.numberAttachmentsEpisodes,
      rescheduledDelivery: chapter.rescheduledDelivery,
      rescheduledRelease: chapter.rescheduledRelease,
      scheduleDelivery: chapter.scheduleDelivery,
      scheduleRelease: chapter.scheduleRelease,
      version: (chapter.version || 0) + 1,
      attachmentTypeId: chapter.attachmentTypeId || 1211,
    }));
  };

  const getChapterAndBlock = (ev) => {
    const selectedId = Number(ev.target.value);

    if (stateEdit.isMaterial) {
      handleEpisodeChange(selectedId);
    } else {
      handleChapterChange(selectedId);
    }
  };

  const getAttachmentType = (ev) => {
    const materialId = Number(ev.target.value);
    const selectedMaterial = materialsList.find(
      (material) => material.attachmentTypeId === materialId
    );

    let latestVersion = null;
    let firstPlanning = null;

    if (selectedMaterial?.plannings?.length) {
      const allVersions = selectedMaterial.plannings
        .flatMap((planning) => planning.versions)
        .sort((a, b) => b.version - a.version);

      latestVersion = allVersions[0];
      firstPlanning = selectedMaterial.plannings[0];
    }

    setStateEdit({
      ...stateEdit,
      attachmentTypeId: materialId,
      rescheduledDelivery: latestVersion?.rescheduledDelivery || null,
      rescheduledRelease: latestVersion?.rescheduledRelease || null,
      scheduleDelivery: latestVersion?.scheduleDelivery || null,
      scheduleRelease: latestVersion?.scheduleRelease || null,
      chapterNumber: firstPlanning?.chapterOrEpisodeNumber || null,
      chapterOrEpisodeNumber: firstPlanning?.chapterOrEpisodeNumber || null,
      version: null,
    });
  };

  const getMaterialVersion = (ev) => {
    const selectedVersionNumber = Number(ev?.target?.value);

    const allVersionsOfMaterialType = materialsList?.filter(
      (material) => material.type.id === stateEdit.attachmentTypeId
    );

    const selectedVersion = allVersionsOfMaterialType?.find(
      (v) => Number(v.version) === selectedVersionNumber
    );

    setStateEdit((prev) => ({
      ...prev,
      version: selectedVersionNumber,
      ...(prev.isMaterial &&
        selectedVersion !== undefined && {
          newMaterialId: selectedVersion.id,
        }),
    }));
  };

  const editAddendumIsValid = () => {
    const hasSelection =
      stateEdit.chapterIds?.length > 0 || stateEdit.episodeIds?.length > 0;
    const hasDate = !!stateEdit.receivedAt;
    return hasSelection && hasDate;
  };

  const editFilesIsValid = () => {
    if (stateEdit.isMaterial) {
      if (stateEdit.version > 0) {
        if (
          stateEdit.structuringReview &&
          stateEdit.reason !== "placeholder" &&
          stateEdit.reason
        ) {
          return true;
        }
        if (!stateEdit.structuringReview) {
          return true;
        }
        return false;
      }
    } else {
      if (stateEdit.id > 0) {
        if (
          stateEdit.structuringReview &&
          stateEdit.reason !== "placeholder" &&
          stateEdit.reason
        ) {
          return true;
        }
        if (!stateEdit.structuringReview) {
          return true;
        }
        return false;
      }
    }
  };

  const patchEditFile = async (ev) => {
    ev.preventDefault();
    appContext.setShowGlassLoading(true);

    const toISO = (val) => (val ? toLocalISOString(new Date(val)) : null);

    try {
      const attachment = {
        ...stateEdit,
        receivedAt: toISO(stateEdit.receivedAt),
        contentAssessment: toISO(stateEdit.planning?.realizedContentSight),
        productionAssessment: toISO(stateEdit.planning?.realizedProdutionSight),
        diversityAssessment: toISO(stateEdit.planning?.diversityAssessment),
        channelNotes: toISO(stateEdit.planning?.realizedChannelFeedback),
      };

      if (stateEdit.isAddendum && stateEdit.isChapter) {
        await editChapterAddendum({
          addendumId: file.addendumId,
          attachment,
        });
      } else if (stateEdit.isAddendum && stateEdit.isMaterial) {
        await editEpisodeAddendum({
          addendumId: file.addendumId,
          attachment,
        });
      } else if (stateEdit.isMaterial) {
        await editMaterialPlanningAttachment({
          materialId: file.materialId,
          attachmentId: file.id,
          attachment,
        });
      } else if (stateEdit.isChapter) {
        await editBlockChapterPlanningAttachment({
          blockId: file.blockId,
          chapterId: file.chapterId,
          chapterAttachmentId: file.chapterAttachmentId,
          attachment,
        });
      }

      toastSuccess("Arquivo editado com sucesso!");
      reload();
    } catch (error) {
      toastError("Erro ao editar o arquivo!");
    } finally {
      appContext.setShowGlassLoading(false);
      appContext.setCurrentStepBox("closed");
    }
  };

  React.useEffect(() => {
    if (notDefault && !stateEdit.structuringReview) {
      setStateEdit({ ...stateEdit, reason: null });
      setNotDefault(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateEdit]);

  const showAddendumAlert =
    (stateEdit.isChapter && !stateEdit.chapterIds?.length) ||
    (stateEdit.isMaterial && !stateEdit.episodeIds?.length);

  return (
    <form className="editChaptersOrEpisodes">
      {stateEdit.isAddendum && (
        <Box sx={{ px: "24px", pt: "16px" }}>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#58627D",
              mb: "12px",
              fontFamily: "inherit",
            }}
          >
            Altere as associações ou a data de recebimento do anexo.
          </Typography>

          {showAddendumAlert && (
            <AlertBox
              hasCloseIcon={false}
              text="É necessário que exista pelo menos uma relação associada ao adendo para prosseguir."
            />
          )}
        </Box>
      )}

      <UploadedDramaturgy
        state={stateEdit}
        dramaturgyList={dramaturgyList}
        materialsList={materialsList}
        getDate={getDate}
        getVersion={getVersion}
        getChapterAndBlock={getChapterAndBlock}
        getReason={getReason}
        setStructuringReview={setStructuringReview}
        isChapter={stateEdit.isChapter}
        isMaterial={stateEdit.isMaterial}
        isAddendumEdit={stateEdit.isAddendum}
        initialAddendumIds={initialAddendumIds}
        setEpisodeIds={setEpisodeIds}
        hideMaterialType={true}
        freeVersionInput={stateEdit.isChapter}
        getAttachmentType={getAttachmentType}
        getMaterialVersion={getMaterialVersion}
        {...props}
      />

      <div className="attachDates">
        <FormAndFields
          action={action}
          handleChangeByInputName={handleChangeByInputName}
          handleSubmit={patchEditFile}
          values={stateEdit}
          handleValidate={
            stateEdit.isAddendum ? editAddendumIsValid : editFilesIsValid
          }
          handleStartDates={handleStartDates}
          handleMaxDates={handleMaxDates}
          chapterOrEpisode={null}
          planningData={
            stateEdit.isAddendum
              ? {
                  ...typeValues,
                  dateFields: { ...typeValues.dateFields, [action]: [] },
                }
              : typeValues
          }
          isChapter={stateEdit.isChapter}
        />
      </div>
    </form>
  );
};

export default EditPlanningAttachment;
